import styled, { css } from 'styled-components';
import { CpdsTabNavItemProps, TabTypes, TabContainerProps } from '../typings';
import { themeToken } from '../../utils/cpds-utils';
import { cpdsMediaQueryVals } from 'src/components/common/cpds/token/shared/common/common.token';
import { checkValue } from 'src/components/common/helper';

const TabNavItemHighlighter = styled.div`
  block-size: 2px;
  inset-block-end: 0;
  inset-inline-start: 0;
  position: absolute;
  transform: scaleY(1);
  transform-origin: center bottom;
  transition: transform 275ms ease, inline-size 275ms ease, inset-inline-start 275ms ease, inset-block-start 275ms ease;
  ${(props) => props?.customStyles}
`;

const TabNavItemWrapper = styled.li`
  display: flex;
  height: 100%;
`;

const TabNavItem = styled.a.attrs((props) => ({
  className: `cpds-tab-nav-item ${props?.selected ? 'selected' : ''} cpds-meta-roman-medium-book-09`,
}))<CpdsTabNavItemProps>`
  ${({
    theme: {
      styles: { cpds },
    },
    selected = false,
    ...rest
  }) => {
    const { token } = themeToken(cpds, cpds?.themeColorMode, rest?.themeColorMode);

    return css`
      text-decoration: none !important;
      opacity: ${selected ? token.tab_nav_item_selected_opacity : token.tab_nav_item_default_opacity};
      cursor: pointer;
      user-select: none;
      color: ${selected
        ? checkValue(token?.tab_nav_item_selected_color)
          ? token?.tab_nav_item_selected_color
          : token?.tab_nav_item_color
        : token?.tab_nav_item_color};
      &:hover {
        opacity: ${token?.tab_nav_item_selected_opacity};
        ${checkValue(token?.tab_nav_item_selected_color)
          ? css`
              color: ${token?.tab_nav_item_selected_color};
            `
          : ''}
      }
    `;
  }}
`;

const TabsList = styled.ul.attrs({
  className: 'cpds-tabs-list',
})`
  ${({
    theme: {
      styles: { cpds },
    },
    ...rest
  }) => {
    const { token } = themeToken(cpds, cpds?.themeColorMode, rest?.themeColorMode);

    return css`
      position: relative;
      display: flex;
      alig-items: center;
      min-block-size: ${token.tabs_container_min_block_size};
      user-select: none;
      &:before {
        content: '';
        position: absolute;
        bottom: 0;
        height: 2px;
      }
    `;
  }}
`;

const TabPane = styled.div<{ selected: boolean }>`
  padding: 0;
  margin: 0;
  display: ${(props) => (props.selected ? 'block' : 'none')};
`;

const TabContent = styled.div`
  ${({
    theme: {
      styles: { cpds },
    },
    ...rest
  }) => {
    const { token } = themeToken(cpds, cpds?.themeColorMode, rest?.themeColorMode);

    return css`
      padding: ${token.spacing.space_12} 0 0;
      margin: 0;
    `;
  }}
`;

const TabsContainer = styled.div<TabContainerProps>`
  ${({
    theme: {
      styles: { cpds },
    },
    bgTheme = 'LIGHT',
    tabType = TabTypes.NATURAL_WIDTH,
    isScrollable = 'DEFAULT',
    scrollWidth = 0,
    forceScrollTabListInMobile = false,
    tabItemColor,
    tabItemHoverColor,
    tabItemHighlightColor,
    ...rest
  }) => {
    const { token } = themeToken(cpds, cpds?.themeColorMode, rest?.themeColorMode);

    let ulWidth = '100%';
    let pesudoElWidth = '100%';
    let lineColor = 'transparent';

    if (isScrollable === 'SCROLLABLE') {
      ulWidth = `100%`;
      pesudoElWidth = `${scrollWidth}px`;
      lineColor = token.tabs_container_border_bottom_color;
    } else if (isScrollable === 'NOT_SCROLLABLE') {
      ulWidth = 'fit-content';
      pesudoElWidth = '100%';
      lineColor = token.tabs_container_border_bottom_color;
    } else {
      ulWidth = '100%';
      pesudoElWidth = '100%';
      lineColor = 'transparent';
    }

    return css`
      position: relative;
      ${TabsList} {
        &:before {
          background-color: ${lineColor};
        }
      }

      ${TabNavItemHighlighter} {
        background-color: ${checkValue(tabItemHighlightColor)
          ? tabItemHighlightColor
          : token.tab_nav_item_highlighter_color};
      }

      ${TabNavItem} {
        ${checkValue(tabItemColor)
          ? css`
              color: ${tabItemColor} !important;
            `
          : ''}
        ${checkValue(tabItemHoverColor)
          ? css`
              &:hover {
                color: ${tabItemHoverColor} !important;
              }
              &.selected {
                color: ${tabItemHoverColor} !important;
              }
            `
          : ''}
      }

      ${tabType === TabTypes.EQUAL_WIDTH
        ? css`
            ${TabsList} {
              column-gap: 0px;
              padding: ${token.spacing.space_0};
              width: 100%;
              &:before {
                width: 100%;
              }
              ${TabNavItemWrapper} {
                display: flex;
                flex: 1;
                align-items: center;
                justify-content: center;
                ${TabNavItem} {
                  padding: ${token.spacing.space_0} ${token.spacing.space_12};
                  width: 100%;
                  text-align: center;
                }
              }
            }
          `
        : css`
            ${TabsList} {
              width: ${ulWidth};
              column-gap: ${token.spacing.space_24};
              -webkit-overflow-scrolling: touch;
              -ms-overflow-style: none;
              overflow-x: scroll;
              overflow-y: hidden;
              padding-block: ${token.spacing.space_0};
              padding-inline: ${token.spacing.space_0};
              scrollbar-width: none;
              white-space: nowrap;
              cursor: pointer;
              box-sizing: border-box;
              &:before {
                width: ${pesudoElWidth};
              }
            }
          `}

      @media screen and (max-width: ${cpdsMediaQueryVals.cpds_small_upper_limit}) {
        ${tabType === TabTypes.EQUAL_WIDTH && forceScrollTabListInMobile
          ? css`
              ${TabsList} {
                width: 100%;
                column-gap: ${token.spacing.space_24};
                -webkit-overflow-scrolling: touch;
                -ms-overflow-style: none;
                overflow-x: scroll;
                overflow-y: hidden;
                padding-block: ${token.spacing.space_0};
                padding-inline: ${token.spacing.space_0};
                scrollbar-width: none;
                white-space: nowrap;
                cursor: pointer;
                box-sizing: border-box;
                &:before {
                  width: ${pesudoElWidth};
                }
                ${TabNavItemWrapper} {
                  width: 100%;
                  flex: auto;
                }
              }
            `
          : ''}
      }
      ${(props) => props?.styleOverrides}
    `;
  }}
`;

const TabsHeader = styled.div.attrs({
  className: 'cpds-tabs-header',
})`
${({
  theme: {
    styles: { cpds },
  },
  ...rest
}) => {
  const { token } = themeToken(cpds, cpds?.themeColorMode, rest?.themeColorMode);

  return css`
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: ${token.spacing.space_40};

    @media (max-width: ${cpdsMediaQueryVals.cpds_medium_lower_limit}) {
      flex-direction: column;
      align-items: flex-start;
      gap: ${token.spacing.space_12};
    }

    .cpds-section-title {
      flex: auto;
    }

    .cpds-tabs-list {
      flex: none;
    }
  `;
}}
`;

export { TabsContainer, TabsList, TabNavItem, TabNavItemHighlighter, TabNavItemWrapper, TabContent, TabPane, TabsHeader };
